import {
  ApiError,
  chunkForImport,
  depthOrdered,
  type ImportRow,
  jsonByteLength,
  type ParsedCsv,
  parseCsv,
  queryKeys,
  serializeCsv,
} from '@mevaser/shared'
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  useToast,
} from '@mevaser/ui'
import type { QueryKey } from '@tanstack/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Download, Upload } from 'lucide-react'
import { type ReactNode, useRef, useState } from 'react'
import { api } from '../lib/api.js'

/** One target field a CSV column can be mapped onto. */
export interface ImportColumn {
  /** The key sent to the server — matches the shared schema's field name. */
  key: string
  label: string
  required?: boolean
  /** Key of another column that waives this one's `required` when it is
   *  mapped (at the map step) and has a value (per row) — e.g. Type isn't
   *  needed on a row that names a parent, since it inherits the parent's. */
  requiredUnless?: string
  /** Normalized (lowercase, alphanumeric-only) header synonyms, used to
   *  pre-fill the mapping so most files need no manual remapping at all. */
  synonyms: string[]
  /** Cell text → typed value, or `null` when the cell cannot be read as this
   *  field. `null` is treated as "not provided" — a required column with a
   *  null value flags the row in the preview step; an optional one is
   *  simply left out of what gets sent. */
  transform: (raw: string) => string | number | boolean | null
  /** When true, two valid rows sharing this column's value are flagged in
   *  the preview instead of both being submitted — an early heads-up for the
   *  one field the server treats as a hard duplicate key. */
  dedupe?: boolean
  /** How to normalize the value before comparing it for `dedupe` — defaults
   *  to a plain lowercase string. Phone needs its own (digits-only) key so a
   *  file with "(718) 555-0143" and "718-555-0143" on two rows is still
   *  caught, matching the server's normalized-phone dedupe. */
  dedupeKey?: (value: string | number | boolean) => string
}

const IGNORE = '__ignore__'

function normalizeHeader(header: string): string {
  return header.toLowerCase().replace(/[^a-z0-9]/g, '')
}

interface ImportResult {
  created: Array<{ index: number; id: string; key: string }>
  updated: Array<{ index: number; id: string; key: string }>
  failed: Array<{ index: number; key: string | null; reason: string }>
}

interface Row {
  originalIndex: number
  data: Record<string, string | number | boolean>
  problems: string[]
}

function buildRows(parsed: ParsedCsv, mapping: Record<string, string>, columns: ImportColumn[]) {
  const headerForKey = new Map(Object.entries(mapping).map(([header, key]) => [key, header]))
  const dedupeSeen = new Map<string, Set<string>>()

  const rows: Row[] = parsed.rows.map((cells, originalIndex) => {
    const data: Record<string, string | number | boolean> = {}
    const problems: string[] = []

    for (const column of columns) {
      const header = headerForKey.get(column.key)
      if (!header) continue
      const cellIndex = parsed.headers.indexOf(header)
      const raw = cellIndex >= 0 ? (cells[cellIndex] ?? '').trim() : ''
      if (!raw) continue
      const value = column.transform(raw)
      if (value === null) continue
      data[column.key] = value

      if (column.dedupe) {
        const seen = dedupeSeen.get(column.key) ?? new Set<string>()
        dedupeSeen.set(column.key, seen)
        const dedupeValue = column.dedupeKey ? column.dedupeKey(value) : String(value).toLowerCase()
        if (seen.has(dedupeValue))
          problems.push(`Duplicate ${column.label.toLowerCase()} in this file`)
        seen.add(dedupeValue)
      }
    }

    const missing = columns
      .filter(
        (c) => c.required && !(c.key in data) && !(c.requiredUnless && c.requiredUnless in data),
      )
      .map((c) => c.label)
    if (missing.length > 0) problems.push(`Missing ${missing.join(', ')}`)

    return { originalIndex, data, problems }
  })

  return rows
}

function downloadTemplate(headers: string[], examples: string[][]) {
  const csv = serializeCsv(headers, examples)
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'import-template.csv'
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

/** Fills `defaults` into any field the row leaves empty. */
function withDefaults(
  data: Record<string, unknown>,
  defaults: Record<string, string | null> | undefined,
): Record<string, unknown> {
  if (!defaults) return data
  const out = { ...data }
  for (const [key, value] of Object.entries(defaults)) {
    if (value !== null && (out[key] === undefined || out[key] === null || out[key] === '')) {
      out[key] = value
    }
  }
  return out
}

interface CsvImportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  endpoint: string
  columns: ImportColumn[]
  invalidateKeys: QueryKey[]
  updateHint: string
  templateHeaders: string[]
  templateExampleRows: string[][]
  entityNamePlural: string
  /** Reorder valid rows right before they're split into request chunks — e.g.
   *  products puts parent rows ahead of variation rows so a parent can never
   *  end up in a later chunk than a variation that depends on it. */
  presortForSubmit?: (rows: Row[]) => Row[]
  /** Values to fill in on any row that has none for that field — e.g. one
   *  "source" tag for the whole file (t1247). A value in the file wins. */
  rowDefaults?: Record<string, string | null>
  /** Extra controls shown above the column mapping. */
  mapStepExtra?: ReactNode
}

export function CsvImportDialog({
  open,
  onOpenChange,
  title,
  endpoint,
  columns,
  invalidateKeys,
  updateHint,
  templateHeaders,
  templateExampleRows,
  entityNamePlural,
  presortForSubmit,
  rowDefaults,
  mapStepExtra,
}: CsvImportDialogProps) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [step, setStep] = useState<'upload' | 'map' | 'preview' | 'results'>('upload')
  const [text, setText] = useState('')
  const [parsed, setParsed] = useState<ParsedCsv | null>(null)
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [updateExisting, setUpdateExisting] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [progress, setProgress] = useState<{ importedRows: number; totalRows: number } | null>(null)
  const [halted, setHalted] = useState<{ message: string; notAttemptedRows: number } | null>(null)

  const reset = () => {
    setStep('upload')
    setText('')
    setParsed(null)
    setMapping({})
    setUpdateExisting(false)
    setResult(null)
    setProgress(null)
    setHalted(null)
  }

  const handleFile = async (file: File) => setText(await file.text())

  const goToMapping = () => {
    const csv = parseCsv(text)
    if (csv.headers.length === 0 || csv.rows.length === 0) {
      toast.error('Nothing to import', 'That file has no rows under its header.')
      return
    }
    const nextMapping: Record<string, string> = {}
    const taken = new Set<string>()
    for (const header of csv.headers) {
      const normalized = normalizeHeader(header)
      const match = columns.find((c) => !taken.has(c.key) && c.synonyms.includes(normalized))
      if (match) {
        nextMapping[header] = match.key
        taken.add(match.key)
      } else {
        nextMapping[header] = IGNORE
      }
    }
    setParsed(csv)
    setMapping(nextMapping)
    setStep('map')
  }

  const setColumnMapping = (header: string, key: string) => {
    setMapping((current) => {
      const next = { ...current }
      if (key !== IGNORE) {
        // A target field can only come from one column — the header that had
        // it before gives it up rather than the row silently carrying it twice.
        for (const [otherHeader, otherKey] of Object.entries(next)) {
          if (otherHeader !== header && otherKey === key) next[otherHeader] = IGNORE
        }
      }
      next[header] = key
      return next
    })
  }

  const mappedKeys = new Set(Object.values(mapping))
  const missingRequired = columns.filter(
    (c) =>
      c.required &&
      !mappedKeys.has(c.key) &&
      !(c.requiredUnless && mappedKeys.has(c.requiredUnless)),
  )

  const rows = parsed ? buildRows(parsed, mapping, columns) : []
  const validRows = rows.filter((r) => r.problems.length === 0)
  const problemRows = rows.filter((r) => r.problems.length > 0)
  const mappedColumns = columns.filter((c) => Object.values(mapping).includes(c.key))

  const submit = useMutation({
    mutationFn: async () => {
      const submitRows = presortForSubmit ? presortForSubmit(validRows) : validRows
      const chunks = chunkForImport(submitRows, (r) => jsonByteLength(r.data))

      const merged: ImportResult = { created: [], updated: [], failed: [] }
      let importedRows = 0
      setProgress({ importedRows: 0, totalRows: submitRows.length })

      for (const [chunkIndex, chunk] of chunks.entries()) {
        let res: ImportResult
        try {
          res = await api.post<ImportResult>(endpoint, {
            updateExisting,
            rows: chunk.map((r) => withDefaults(r.data, rowDefaults)) as ImportRow[],
          })
        } catch (error) {
          if (chunkIndex === 0) throw error // nothing committed yet — surface as today's error toast
          const message = error instanceof ApiError ? error.message : 'The request failed'
          return {
            result: merged,
            halted: { message, notAttemptedRows: submitRows.length - importedRows },
          }
        }
        // Server `index` values are positions within this chunk — remap them
        // back to the row's place in the original CSV before merging.
        const originalIndexOf = (i: number) => chunk[i]?.originalIndex ?? i
        for (const c of res.created) merged.created.push({ ...c, index: originalIndexOf(c.index) })
        for (const u of res.updated) merged.updated.push({ ...u, index: originalIndexOf(u.index) })
        for (const f of res.failed) merged.failed.push({ ...f, index: originalIndexOf(f.index) })
        importedRows += chunk.length
        setProgress({ importedRows, totalRows: submitRows.length })
      }

      return { result: merged, halted: null }
    },
    onSuccess: async ({ result: res, halted: haltInfo }) => {
      res.failed.sort((a, b) => a.index - b.index)
      setResult(res)
      setHalted(haltInfo)
      setStep('results')
      for (const key of invalidateKeys) await queryClient.invalidateQueries({ queryKey: key })

      if (haltInfo) {
        toast.toast({
          tone: 'warning',
          title: 'Import stopped partway through',
          description: `${haltInfo.message} — ${haltInfo.notAttemptedRows} row${haltInfo.notAttemptedRows === 1 ? '' : 's'} not attempted; earlier rows were already saved.`,
        })
        return
      }

      const parts: string[] = []
      if (res.created.length) parts.push(`${res.created.length} created`)
      if (res.updated.length) parts.push(`${res.updated.length} updated`)
      if (res.failed.length === 0) {
        toast.success(parts.join(', ') || 'Import complete')
      } else {
        toast.toast({
          tone: 'warning',
          title: parts.join(', ') || 'Partially imported',
          description: `${res.failed.length} row${res.failed.length === 1 ? '' : 's'} need attention — see below.`,
        })
      }
    },
    onError: (error) =>
      toast.error('Could not import', error instanceof ApiError ? error.message : undefined),
  })

  const lineFor = (originalIndex: number) => parsed?.lines[originalIndex] ?? originalIndex + 2

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // A multi-chunk import is several sequential requests — closing
        // partway through would abandon it with no way back to the results.
        if (!next && submit.isPending) return
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {step === 'upload' ? (
            <>
              <Field
                label="Paste a CSV, or choose a file"
                hint="The first row must be column headers."
              >
                {(props) => (
                  <Textarea
                    {...props}
                    rows={8}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={`${templateHeaders.join(',')}\n${templateExampleRows.map((r) => r.join(',')).join('\n')}`}
                  />
                )}
              </Field>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv,text/plain"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    // Import requests are chunked automatically, so this only
                    // needs to keep an implausible upload from freezing the tab.
                    if (file.size > 10_000_000) {
                      toast.error('File is too large', 'That file is over 10 MB.')
                    } else {
                      await handleFile(file)
                    }
                  }
                  e.target.value = ''
                }}
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload /> Choose file…
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => downloadTemplate(templateHeaders, templateExampleRows)}
                >
                  <Download /> Download template
                </Button>
              </div>
            </>
          ) : null}

          {step === 'map' && parsed ? (
            <>
              <p className="text-sm text-foreground-muted">
                Match each column in your file to a field. Columns set to "Ignore" are left out.
              </p>
              {mapStepExtra}
              <div className="max-h-[50vh] space-y-2 overflow-y-auto scrollbar-thin">
                {parsed.headers.map((header, i) => (
                  <div
                    key={header}
                    className="flex items-center gap-3 rounded-md border border-border p-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{header}</p>
                      <p className="truncate text-xs text-foreground-muted">
                        e.g. {parsed.rows[0]?.[i] || '—'}
                      </p>
                    </div>
                    <Select
                      value={mapping[header] ?? IGNORE}
                      onValueChange={(v) => setColumnMapping(header, v)}
                    >
                      <SelectTrigger className="w-52">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={IGNORE}>Ignore this column</SelectItem>
                        {columns.map((c) => (
                          <SelectItem key={c.key} value={c.key}>
                            {c.label}
                            {c.required ? ' *' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              {missingRequired.length > 0 ? (
                <Alert tone="warning">
                  Still needs a column for: {missingRequired.map((c) => c.label).join(', ')}.
                </Alert>
              ) : null}
            </>
          ) : null}

          {step === 'preview' && parsed ? (
            <>
              <p className="text-sm">
                <span className="font-medium">{validRows.length}</span> row
                {validRows.length === 1 ? '' : 's'} ready to import
                {problemRows.length > 0 ? (
                  <span className="text-foreground-muted">
                    {' '}
                    · {problemRows.length} will be skipped
                  </span>
                ) : null}
              </p>

              {validRows.length > 0 ? (
                <div className="max-h-64 overflow-auto rounded-md border border-border scrollbar-thin">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-surface-sunken">
                      <tr>
                        {mappedColumns.map((c) => (
                          <th
                            key={c.key}
                            className="whitespace-nowrap px-2 py-1.5 text-start font-medium"
                          >
                            {c.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {validRows.slice(0, 20).map((row) => (
                        <tr key={row.originalIndex} className="border-t border-border">
                          {mappedColumns.map((c) => (
                            <td key={c.key} className="whitespace-nowrap px-2 py-1.5">
                              {row.data[c.key] === undefined ? (
                                <span className="text-foreground-subtle">—</span>
                              ) : (
                                String(row.data[c.key])
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {validRows.length > 20 ? (
                    <p className="px-2 py-1.5 text-xs text-foreground-muted">
                      and {validRows.length - 20} more.
                    </p>
                  ) : null}
                </div>
              ) : null}

              {problemRows.length > 0 ? (
                <Alert tone="warning">
                  <p className="mb-1 font-medium">
                    {problemRows.length} row{problemRows.length === 1 ? '' : 's'} will be skipped
                  </p>
                  <ul className="space-y-0.5 text-xs">
                    {problemRows.slice(0, 15).map((row) => (
                      <li key={row.originalIndex}>
                        Line {lineFor(row.originalIndex)} — {row.problems.join('; ')}
                      </li>
                    ))}
                  </ul>
                  {problemRows.length > 15 ? (
                    <p className="mt-1 text-xs">and {problemRows.length - 15} more.</p>
                  ) : null}
                </Alert>
              ) : null}

              <Field label="Update existing records" hint={updateHint}>
                {(props) => (
                  <Switch {...props} checked={updateExisting} onCheckedChange={setUpdateExisting} />
                )}
              </Field>
            </>
          ) : null}

          {step === 'results' && result ? (
            <>
              {halted ? (
                <Alert tone="warning">
                  Stopped after an error: {halted.message}. {halted.notAttemptedRows} row
                  {halted.notAttemptedRows === 1 ? '' : 's'} were not attempted; the rows below were
                  already saved.
                </Alert>
              ) : null}
              <p className="text-sm font-medium">
                {result.created.length} created, {result.updated.length} updated
                {result.failed.length > 0 ? `, ${result.failed.length} failed` : ''}
              </p>
              {result.failed.length > 0 ? (
                <div className="max-h-64 space-y-1 overflow-y-auto text-xs scrollbar-thin">
                  {result.failed.map((f) => (
                    <p key={f.index}>
                      Line {lineFor(f.index)}
                      {f.key ? ` (${f.key})` : ''} — {f.reason}
                    </p>
                  ))}
                </div>
              ) : (
                <Alert tone="success">
                  All {result.created.length + result.updated.length} {entityNamePlural} imported.
                </Alert>
              )}
            </>
          ) : null}
        </DialogBody>
        <DialogFooter>
          {step === 'upload' ? (
            <>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="button" variant="primary" disabled={!text.trim()} onClick={goToMapping}>
                Continue
              </Button>
            </>
          ) : null}
          {step === 'map' ? (
            <>
              <Button type="button" variant="ghost" onClick={() => setStep('upload')}>
                Back
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={missingRequired.length > 0}
                onClick={() => setStep('preview')}
              >
                Continue
              </Button>
            </>
          ) : null}
          {step === 'preview' ? (
            <>
              <Button
                type="button"
                variant="ghost"
                disabled={submit.isPending}
                onClick={() => setStep('map')}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="primary"
                loading={submit.isPending}
                disabled={validRows.length === 0}
                onClick={() => submit.mutate()}
              >
                {submit.isPending && progress
                  ? `Importing ${progress.importedRows} of ${progress.totalRows} rows…`
                  : `Import ${validRows.length} row${validRows.length === 1 ? '' : 's'}`}
              </Button>
            </>
          ) : null}
          {step === 'results' ? (
            <Button type="button" variant="primary" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const dollarsToCents = (raw: string): number | null => {
  const n = Number.parseFloat(raw.replace(/[$,]/g, ''))
  return Number.isFinite(n) ? Math.round(n * 100) : null
}

const toBoolean = (raw: string): boolean | null => {
  const v = raw.trim().toLowerCase()
  if (['true', 'yes', 'y', '1'].includes(v)) return true
  if (['false', 'no', 'n', '0'].includes(v)) return false
  return null
}

const PRODUCT_TYPES_BY_ALIAS: Record<string, string> = {
  serialized: 'serialized',
  phone: 'serialized',
  phones: 'serialized',
  stock: 'stock',
  accessory: 'stock',
  accessories: 'stock',
  plan: 'plan',
  plans: 'plan',
  service: 'service',
  services: 'service',
  sim: 'sim',
  sims: 'sim',
  'sim card': 'sim',
  'sim cards': 'sim',
  'digital license': 'digital_license',
  'digital licenses': 'digital_license',
  digital_license: 'digital_license',
  license: 'digital_license',
  licenses: 'digital_license',
}

export const PRODUCT_IMPORT_COLUMNS: ImportColumn[] = [
  {
    key: 'sku',
    label: 'SKU',
    required: true,
    synonyms: ['sku'],
    transform: (r) => r || null,
    dedupe: true,
  },
  {
    key: 'parentSku',
    label: 'Parent SKU',
    synonyms: ['parentsku', 'parent', 'parentproduct', 'parentproductsku'],
    transform: (r) => r || null,
  },
  {
    key: 'type',
    label: 'Type',
    required: true,
    requiredUnless: 'parentSku',
    synonyms: ['type', 'producttype'],
    transform: (r) => PRODUCT_TYPES_BY_ALIAS[r.trim().toLowerCase()] ?? null,
  },
  {
    key: 'name',
    label: 'Name',
    required: true,
    synonyms: ['name', 'productname'],
    transform: (r) => r || null,
  },
  {
    key: 'priceCents',
    label: 'Price',
    required: true,
    synonyms: ['price', 'priceusd'],
    transform: dollarsToCents,
  },
  { key: 'costCents', label: 'Cost', synonyms: ['cost', 'costusd'], transform: dollarsToCents },
  {
    key: 'description',
    label: 'Description',
    synonyms: ['description'],
    transform: (r) => r || null,
  },
  { key: 'category', label: 'Category', synonyms: ['category'], transform: (r) => r || null },
  { key: 'brand', label: 'Brand', synonyms: ['brand'], transform: (r) => r || null },
  {
    key: 'imageUrl',
    label: 'Image URL',
    synonyms: ['imageurl', 'image'],
    transform: (r) => r || null,
  },
  { key: 'isActive', label: 'Active', synonyms: ['active', 'isactive'], transform: toBoolean },
  {
    key: 'reorderPoint',
    label: 'Reorder point',
    synonyms: ['reorderpoint'],
    transform: (r) => (/^\d+$/.test(r.trim()) ? Number.parseInt(r, 10) : null),
  },
]

export const CUSTOMER_IMPORT_COLUMNS: ImportColumn[] = [
  {
    key: 'name',
    label: 'Name',
    required: true,
    synonyms: ['name', 'customername', 'fullname'],
    transform: (r) => r || null,
  },
  {
    key: 'businessName',
    label: 'Business / other identifier',
    synonyms: ['businessname', 'business', 'company', 'identifier'],
    transform: (r) => r || null,
  },
  {
    key: 'email',
    label: 'Email',
    synonyms: ['email', 'emailaddress'],
    transform: (r) => r || null,
    dedupe: true,
  },
  {
    key: 'phone',
    label: 'Phone',
    synonyms: ['phone', 'phonenumber', 'cell', 'mobile'],
    transform: (r) => r || null,
    dedupe: true,
    // Digits-only so "(718) 555-0143" and "718-555-0143" on two rows are
    // still caught, matching the server's normalized-phone dedupe.
    dedupeKey: (v) => String(v).replace(/\D/g, ''),
  },
  { key: 'notes', label: 'Notes', synonyms: ['notes'], transform: (r) => r || null },
  {
    key: 'source',
    label: 'Source',
    synonyms: ['source', 'importsource', 'list'],
    transform: (r) => r || null,
  },
  {
    key: 'externalId',
    label: 'External ID (e.g. Square customer ID)',
    synonyms: [
      'externalid',
      'squareid',
      'squarecustomerid',
      'customerid',
      'referenceid',
      'externalcustomerid',
    ],
    transform: (r) => r || null,
  },
  {
    key: 'addressLine1',
    label: 'Address line 1',
    synonyms: ['addressline1', 'address', 'street', 'streetaddress'],
    transform: (r) => r || null,
  },
  {
    key: 'addressLine2',
    label: 'Address line 2',
    synonyms: ['addressline2', 'apt', 'suite'],
    transform: (r) => r || null,
  },
  { key: 'addressCity', label: 'City', synonyms: ['city'], transform: (r) => r || null },
  { key: 'addressState', label: 'State', synonyms: ['state'], transform: (r) => r || null },
  {
    key: 'addressPostalCode',
    label: 'ZIP',
    synonyms: ['zip', 'zipcode', 'postalcode'],
    transform: (r) => r || null,
  },
]

export function ProductImportDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <CsvImportDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Import products"
      endpoint="/products/import"
      columns={PRODUCT_IMPORT_COLUMNS}
      invalidateKeys={[queryKeys.products.all()]}
      updateHint="Match products by SKU. Off by default — a SKU already in the catalog is skipped, not overwritten."
      templateHeaders={['sku', 'type', 'name', 'price', 'category', 'parent sku']}
      templateExampleRows={[
        ['ACC-CASE-01', 'stock', 'Zip Case', '9.99', 'Accessories', ''],
        ['ACC-CASE-01-RED', '', 'Zip Case — Red', '9.99', '', 'ACC-CASE-01'],
      ]}
      entityNamePlural="products"
      // A variation's parent might appear later in the file than the
      // variation itself, and a variation may have variations of its own —
      // order rows by their depth in the file's own parent chain so a parent
      // is never chunked into a later request than the rows that depend on it.
      presortForSubmit={(rows) =>
        depthOrdered(
          rows,
          (r) => (typeof r.data.sku === 'string' ? r.data.sku.trim() || null : null),
          (r) => (typeof r.data.parentSku === 'string' ? r.data.parentSku.trim() || null : null),
        )
      }
    />
  )
}

export function CustomerImportDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  // One tag for every row of the file that has no source of its own (t1247).
  const [source, setSource] = useState('')
  return (
    <CsvImportDialog
      rowDefaults={source.trim() ? { source: source.trim() } : {}}
      mapStepExtra={
        <Field
          label="Source for every row (optional)"
          hint='Where this file came from, e.g. "Square export" or "Google Contacts 2026-10". Shown on each customer and filterable on the Customers page.'
        >
          {(props) => (
            <Input
              {...props}
              value={source}
              onChange={(e) => setSource(e.target.value)}
              maxLength={200}
              placeholder="Square export"
            />
          )}
        </Field>
      }
      open={open}
      onOpenChange={onOpenChange}
      title="Import customers"
      endpoint="/customers/import"
      columns={CUSTOMER_IMPORT_COLUMNS}
      invalidateKeys={[queryKeys.customers.all()]}
      updateHint="Match customers by email, then phone (then external ID, when a row has neither). Off by default — a match is skipped, not overwritten."
      templateHeaders={['name', 'email', 'phone', 'city', 'state', 'zip']}
      templateExampleRows={[
        ['Yosef Klein', 'yosef@example.com', '(718) 555-0143', 'Lakewood', 'NJ', '08701'],
      ]}
      entityNamePlural="customers"
    />
  )
}
